import { useState, useEffect, useMemo, PropsWithChildren } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button, DialogTitle, Stack } from '@mui/material';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useTranslation from 'shared/hooks/useTranslation';
import formatDate from 'shared/utils/formatDate';
import getSelectHelpers from 'shared/utils/getSelectHelpers';
import { useUpdateCaseNotifications } from 'shared/hooks/useUpdateCaseNotifications';

import { Dropzone } from 'features/file-loader';
import { userSpaceRenovationRequestsApi } from '../../api';

import PageLayout from 'layouts/PageLayout';
import DatePicker from 'components/controls/date-picker';
import ValidationWarning from 'components/validation-warning';
import Checkbox from 'shared/components/Checkbox';
import ControlLabel from 'shared/components/ControlLabel';
import Dialog from 'shared/components/Dialog';
import LoadingButton from 'shared/components/LoadingButton';
import Select from 'shared/components/Select';
import SuccessfullyCreatedCaseDialog from 'shared/components/SuccessfullyCreatedCaseDialog';
import UnsavedChangesDialog from 'shared/components/UnsavedChangesDialog';

import type { FormState } from './SpaceRenovationRequestCreateForm.type';
import { ValidationWarningMessage } from './constants';
import {
  createBaseRenovationType,
  getRenovationTypeOptions,
  prepareRenovationTypes,
} from './helpers';
import Instruction from './Instruction';
import RenovationTypes from './RenovationTypes';
import { UserWarning } from './SpaceRenovationRequestCreateForm.styled';

const CheckboxContainer = ({ children }: PropsWithChildren<{}>) => (
  <Stack alignItems="center" direction="row" spacing={1.5}>
    {children}
  </Stack>
);

type SpaceRenovationRequestCreateFormProps = {
  onClose: () => void;
};

const SpaceRenovationRequestCreateForm = ({
  onClose: onExternalClose,
}: SpaceRenovationRequestCreateFormProps) => {
  const { t } = useTranslation();
  const coopId = useCooperativeId();
  const history = useHistory();

  const [showedFormDialog, showFormDialog] = useState(true);
  const [showedSuccessDialog, showSuccessDialog] = useState(false);
  const [showedUnsavedChangesDialog, showUnsavedChangesDialog] =
    useState(false);
  const updateCaseNotifications = useUpdateCaseNotifications();

  const [getCreateFormOptions, { data, isLoading, isFetching, isError }] =
    userSpaceRenovationRequestsApi.useLazyGetUserSpaceRenovationRequestCreateFormOptionsQuery();
  const [
    createSpaceRenovationRequest,
    { data: newSpaceRenovationRequest, isLoading: isCreating, isSuccess },
  ] = userSpaceRenovationRequestsApi.useCreateSpaceRenovationRequestMutation();

  const formMethods = useForm<FormState>({
    criteriaMode: 'firstError',
    defaultValues: {
      endDate: null,
      files: [],
      readInstructions: false,
      renovationTypes: [createBaseRenovationType()],
      sendEmail: true,
      sendSms: false,
      startDate: null,
      spaceId: null,
    },
    reValidateMode: 'onSubmit',
  });
  const {
    clearErrors,
    control,
    formState: { errors, isDirty },
    handleSubmit,
    watch,
  } = formMethods;

  const handleCloseDialog = () => {
    if (isDirty) {
      showUnsavedChangesDialog(true);
    } else {
      showFormDialog(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    showSuccessDialog(false);
    showFormDialog(false);
  };

  const handleCloseSuccessDialogWithRedirect = () => {
    showSuccessDialog(false);
    showFormDialog(false);

    if (isSuccess && newSpaceRenovationRequest) {
      history.push(`/repair/${newSpaceRenovationRequest.Id}`);
    }
  };

  const handleCloseFormWithoutSaving = () => {
    showUnsavedChangesDialog(false);
    showFormDialog(false);
  };

  const handleCloseUnsavedChangesDialog = () => {
    showUnsavedChangesDialog(false);
  };

  const spaceSelectHelpers = useMemo(
    () => getSelectHelpers(data?.Spaces ?? []),
    [data?.Spaces]
  );

  const renovationTypeOptions = useMemo(
    () => getRenovationTypeOptions(data?.RenovationTypesCodes ?? []),
    [data?.RenovationTypesCodes]
  );

  const renovationTypeExecutorCodes = useMemo(
    () => data?.RenovationTypeExecutorsCodes ?? [],
    [data?.RenovationTypeExecutorsCodes]
  );

  const submitForm = (formData: FormState) => {
    if (
      !formData.endDate ||
      !formData.spaceId ||
      !formData.startDate ||
      !coopId
    )
      return;

    createSpaceRenovationRequest({
      body: {
        AirSourceHeatPumpAssembly: data?.AirSourceHeatPumpAssembly ?? false,
        BalconyOrTerraceGlazing: data?.BalconyOrTerraceGlazing ?? false,
        ChangesOnFloorPlan: data?.ChangesOnFloorPlan ?? false,
        ChangesOnWetSpaces: data?.ChangesOnWetSpaces ?? false,
        Description: data?.Description ?? null,
        Electrics: data?.Electrics ?? false,
        EndDate: formatDate(formData.endDate, { patternName: 'server' }),
        HasDocuments: !!formData.files.length,
        OtherMinorChange: data?.OtherMinorChange ?? false,
        OtherOrdinaryChange: data?.OtherOrdinaryChange ?? false,
        OtherSignificantChange: data?.OtherSignificantChange ?? false,
        Painting: data?.Painting ?? false,
        Plumbing: data?.Plumbing ?? false,
        RenewalOfFloorCoating: data?.RenewalOfFloorCoating ?? false,
        RenewalOfInteriorDoors: data?.RenewalOfInteriorDoors ?? false,
        RenovationTypes: prepareRenovationTypes(formData.renovationTypes),
        SendEmail: formData.sendEmail,
        SendSms: formData.sendSms,
        SpaceId: formData.spaceId,
        StartDate: formatDate(formData.startDate, { patternName: 'server' }),
        Wallpapering: data?.Wallpapering ?? false,
      },
      files: formData.files,
      cooperativeId: coopId,
    });
  };

  useEffect(() => {
    if (coopId) {
      getCreateFormOptions(coopId);
    }
  }, [coopId]);

  useEffect(() => {
    if (isSuccess) {
      showSuccessDialog(true);
      updateCaseNotifications();
    }
  }, [isSuccess]);

  return (
    <Dialog
      open={showedFormDialog}
      fullWidth
      maxWidth="lg"
      onClose={handleCloseDialog}
      TransitionProps={{
        onExited: onExternalClose,
      }}
    >
      <UnsavedChangesDialog
        open={showedUnsavedChangesDialog}
        onApply={handleCloseFormWithoutSaving}
        onReject={handleCloseUnsavedChangesDialog}
      />
      {newSpaceRenovationRequest && (
        <SuccessfullyCreatedCaseDialog
          description="Palvelupyyntö on lähetetty"
          redirectiButtonLabel={`# ${newSpaceRenovationRequest.Title}`}
          open={showedSuccessDialog}
          onApply={handleCloseSuccessDialogWithRedirect}
          onClose={handleCloseSuccessDialog}
        />
      )}
      <FormProvider {...formMethods}>
        <PageLayout isLoading={isLoading || isFetching} isError={isError}>
          <Stack
            component="form"
            spacing={2.5}
            onSubmit={handleSubmit(submitForm)}
          >
            <DialogTitle>Uusi muutostyöilmoitus</DialogTitle>
            <Instruction />
            <Stack
              direction={{
                md: 'row',
                xs: 'column',
              }}
              spacing={1.5}
            >
              <Stack flex={2}>
                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('##attribute#incident#uds_spaceid')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.spaceId?.message}
                    handleInitErrors={clearErrors}
                    Component={
                      <Controller
                        control={control}
                        name="spaceId"
                        rules={{
                          required: ValidationWarningMessage.Required,
                        }}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            value={value ?? undefined}
                            variant="underline"
                            options={spaceSelectHelpers.options}
                            getOptionLabel={spaceSelectHelpers.getOptionLabel}
                            onChange={onChange}
                          />
                        )}
                      />
                    }
                  />
                </Stack>
              </Stack>
              <Stack
                flex={{
                  md: 1.8,
                  xs: 1,
                }}
                direction={{
                  sm: 'row',
                  xs: 'column',
                }}
                spacing={1.5}
              >
                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('##attribute#incident#uds_aloituspiva')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.startDate?.message}
                    handleInitErrors={clearErrors}
                    Component={
                      <Controller
                        control={control}
                        name="startDate"
                        rules={{
                          required: ValidationWarningMessage.Required,
                        }}
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            date={value}
                            handleChange={onChange}
                            maxDate={watch('endDate')}
                            variant="underline"
                            startFrom={data?.FormMetadata?.DateMinYear}
                            endIn={data?.FormMetadata?.DateMaxYear}
                          />
                        )}
                      />
                    }
                  />
                </Stack>

                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('##attribute#incident#uds_pttymispiv')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.endDate?.message}
                    handleInitErrors={clearErrors}
                    Component={
                      <Controller
                        control={control}
                        name="endDate"
                        rules={{
                          required: ValidationWarningMessage.Required,
                        }}
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            date={value}
                            handleChange={onChange}
                            minDate={watch('startDate')}
                            variant="underline"
                            startFrom={data?.FormMetadata?.DateMinYear}
                            endIn={data?.FormMetadata?.DateMaxYear}
                          />
                        )}
                      />
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
            <RenovationTypes
              descriptionsMaxLength={
                data?.FormMetadata.DescriptionMaxLength ?? Infinity
              }
              foreignRegistrationNumberMaxLength={
                data?.FormMetadata.ForeignRegistrationNumberMaxLength ??
                Infinity
              }
              organizationNameMaxLength={
                data?.FormMetadata.OrganizationNameMaxLength ?? Infinity
              }
              executorCodes={renovationTypeExecutorCodes}
              renovationTypeOptions={renovationTypeOptions}
            />
            <CheckboxContainer>
              <Controller
                control={control}
                name="sendSms"
                render={({ field: { value, onChange } }) => (
                  <Checkbox checked={value} onChange={onChange} />
                )}
              />
              <ControlLabel>
                Haluan saada tekstiviestejä työn etenemisestä
              </ControlLabel>
            </CheckboxContainer>
            <CheckboxContainer>
              <Controller
                control={control}
                name="sendEmail"
                render={({ field: { value, onChange } }) => (
                  <Checkbox checked={value} onChange={onChange} />
                )}
              />
              <ControlLabel>
                Haluan saada sähköposteja työn etenemisestä
              </ControlLabel>
            </CheckboxContainer>
            <Stack spacing={1} maxWidth={{ xs: '100%', md: '50%' }}>
              <ControlLabel>Liitteet</ControlLabel>
              <Controller
                control={control}
                name="files"
                render={({ field: { value, onChange } }) => (
                  <Dropzone value={value} onChange={onChange} />
                )}
              />
            </Stack>
            <CheckboxContainer>
              <ValidationWarning
                text={errors.readInstructions?.message}
                handleInitErrors={clearErrors}
                Component={
                  <Controller
                    control={control}
                    name="readInstructions"
                    rules={{
                      validate: {
                        required: (value) =>
                          value || ValidationWarningMessage.Required,
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox checked={value} onChange={onChange} />
                    )}
                  />
                }
              />
              <ControlLabel required>Olen lukenut muutostyöohjeen</ControlLabel>
            </CheckboxContainer>
            <UserWarning>
              Huom. Muutostöitä ei saa aloittaa ennen kuin muutostyöilmoitus on
              hyväksytty
            </UserWarning>
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button color="neutral" onClick={handleCloseDialog}>
                {t('#portal.common.button.cancel')}
              </Button>
              <LoadingButton
                color="success"
                type="submit"
                loading={isCreating}
                disabled={isCreating}
              >
                {t('Send')}
              </LoadingButton>
            </Stack>
          </Stack>
        </PageLayout>
      </FormProvider>
    </Dialog>
  );
};

export default SpaceRenovationRequestCreateForm;
